import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Flex,
  Input,
  IconButton,
  HStack,
  Heading,
  Text,
  Badge,
  useColorModeValue,
  InputGroup,
  InputRightElement,
  Button,
  Wrap,
  WrapItem,
  useToast,
  Icon,
  useBreakpointValue,
  ScaleFade,
  Tooltip,
} from "@chakra-ui/react";
import { FiSend, FiMessageSquare, FiX, FiLogOut, FiStar } from "react-icons/fi";
import { useTranslation } from 'react-i18next';
import { apiClient } from '../../lib/apiClient';
import logger from '../../utils/logger';
import { ChatMessage } from './ChatMessage';
import { WordBubble } from './WordBubble';
import { colors } from '../../assets/styles/colors';
import { keyframes } from '@emotion/react';
import confetti from "canvas-confetti";

// Animation keyframes
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

// Define the pulse animation for the typing indicator
const pulseAnimation = keyframes`
  0% { transform: scale(0.95); opacity: 0.7; }
  50% { transform: scale(1); opacity: 1; }
  100% { transform: scale(0.95); opacity: 0.7; }
`;

// Inline TypingIndicator component
const TypingIndicator = () => {
  const typingDotColor = useColorModeValue("gray.400", "gray.500");

  return (
    <Box alignSelf="flex-start" px={4} py={2}>
      <HStack spacing={2}>
        <Box
          h="10px"
          w="10px"
          borderRadius="full"
          bg={typingDotColor}
          animation={`${pulseAnimation} 1s infinite`}
          style={{ animationDelay: "0s" }}
        />
        <Box
          h="10px"
          w="10px"
          borderRadius="full"
          bg={typingDotColor}
          animation={`${pulseAnimation} 1s infinite`}
          style={{ animationDelay: "0.2s" }}
        />
        <Box
          h="10px"
          w="10px"
          borderRadius="full"
          bg={typingDotColor}
          animation={`${pulseAnimation} 1s infinite`}
          style={{ animationDelay: "0.4s" }}
        />
      </HStack>
    </Box>
  );
};

// Types
interface ChatInterfaceProps {
  words: Array<{
    id: string;
    word: string;
    language: string;
    translation?: string;
    translation_language?: string;
    user_id: string;
  }>;
  targetLanguage: string;
  nativeLanguage: string;
  onEndSession: () => void;
  onUpdateMessages?: (messages: ChatMessage[]) => void;
  onWordUsed?: (wordId: string, wordText: string) => void;
  onBurgerClick?: () => void;
}

interface ChatMessage {
  role: "user" | "assistant" | "system";
  content: string;
}

export function ChatInterface({ words, targetLanguage, nativeLanguage, onEndSession, onUpdateMessages, onWordUsed, onBurgerClick }: ChatInterfaceProps) {
  const { t } = useTranslation();
  const toast = useToast();

  // Refs
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const messageSoundRef = useRef<HTMLAudioElement | null>(null);
  const confettiCanvasRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const didInitializeRef = useRef(false);

  // States
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputMessage, setInputMessage] = useState("");
  const [isAiResponding, setIsAiResponding] = useState(false);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [suggestedWords, setSuggestedWords] = useState<typeof words>([]);
  const [showEndSessionConfirm, setShowEndSessionConfirm] = useState(false);
  const [, setIsSidebarOpen] = useState(false);
  const [streakPoints, setStreakPoints] = useState(0);

  // Color mode values
  const chatContainerBg = useColorModeValue("gray.50", colors.primary[700]);
  const headerFooterBg = useColorModeValue("white", colors.primary[700]);
  const borderColor = useColorModeValue("gray.200", colors.primary[600]);
  const inputBg = useColorModeValue("white", colors.primary[600]);
  const chatBoxShadow = useColorModeValue("sm", "none");
  const accentColor = useColorModeValue("purple.500", "purple.300");
  const streakBg = useColorModeValue("purple.50", "purple.900");

  // Additional color values for confirmation dialog
  const confirmationBg = useColorModeValue("gray.100", colors.primary[600]);
  const confirmationBorderColor = useColorModeValue(
    "gray.200",
    colors.primary[500]
  );
  const iconBg = useColorModeValue("white", colors.primary[700]);

  // Scrollbar colors
  const scrollbarTrackBg = useColorModeValue("gray.100", colors.primary[800]);
  const scrollbarThumbBg = useColorModeValue("gray.300", colors.primary[500]);
  const scrollbarThumbHoverBg = useColorModeValue(
    "gray.400",
    colors.primary[400]
  );

  // Chat container styles
  const chatContainerStyles = {
    "&::-webkit-scrollbar": {
      width: "8px",
      borderRadius: "8px",
    },
    "&::-webkit-scrollbar-track": {
      background: scrollbarTrackBg,
      borderRadius: "8px",
    },
    "&::-webkit-scrollbar-thumb": {
      background: scrollbarThumbBg,
      borderRadius: "8px",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      background: scrollbarThumbHoverBg,
    },
    height: "100%",
    width: "100%",
    overflowY: "auto",
  };

  // Determine if the layout should be RTL
  const isRTL = useMemo(() => {
    return document.documentElement.dir === 'rtl';
  }, []);

  // Function to trigger confetti animation
  const triggerConfetti = useCallback(() => {
    if (typeof confetti === "function") {
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 },
        colors: ["#8B5CF6", "#EC4899", "#3B82F6", "#10B981"],
      });
    }
  }, []);

  // Play message sound helper
  const playMessageSound = useCallback(() => {
    if (messageSoundRef.current) {
      messageSoundRef.current.currentTime = 0;
      messageSoundRef.current.play().catch((err) => {
        logger.warn("Error playing message sound:", err);
      });
    }
  }, []);

  // Initialize sound effect
  useEffect(() => {
    messageSoundRef.current = new Audio("/sounds/message.mp3");

    return () => {
      messageSoundRef.current = null;
    };
  }, []);

  // Update isSidebarOpen whenever the sidebar state changes in the Layout component
  useEffect(() => {
    // Check if sidebar is open by checking the chat-active class
    const checkSidebar = () => {
      const sidebarElement = document.querySelector('[data-sidebar="open"]');
      setIsSidebarOpen(!!sidebarElement);
    };

    // Initial check
    checkSidebar();

    // Create a MutationObserver to watch for changes to the DOM
    const observer = new MutationObserver(() => {
      checkSidebar();
    });

    // Start observing the document body for added/removed classes
    observer.observe(document.body, {
      attributes: true,
      childList: true,
      subtree: true,
    });

    // Add event listener for the custom sidebar toggle event
    const handleSidebarToggle = () => {
      setIsSidebarOpen((prev) => !prev);
    };

    document.addEventListener("toggle-sidebar", handleSidebarToggle);
    document.addEventListener("chat-toggle-sidebar", handleSidebarToggle);

    // Add chat-active class when component mounts
    document.body.classList.add("chat-active");

    return () => {
      observer.disconnect();
      document.removeEventListener("toggle-sidebar", handleSidebarToggle);
      document.removeEventListener("chat-toggle-sidebar", handleSidebarToggle);
      document.body.classList.remove("chat-active");
    };
  }, []);

  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages, playMessageSound]);

  // Scroll chat container to bottom
  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  // Update suggested words - choose 5 random words from the user's word list
  const updateSuggestedWords = useCallback(() => {
    if (!words || words.length === 0) return;

    // Shuffle the words array and take the first 5 items
    const shuffled = [...words].sort(() => 0.5 - Math.random());
    setSuggestedWords(shuffled.slice(0, 5));
  }, [words]);

  // Update messages with notification to parent
  const updateMessages = useCallback(
    (newMessages: ChatMessage[]) => {
      setMessages(newMessages);
      if (onUpdateMessages) {
        onUpdateMessages(newMessages);
      }

      // Play sound for new assistant messages that aren't empty or confirmation messages
      const lastMessage = newMessages[newMessages.length - 1];
      if (
        lastMessage?.role === "assistant" &&
        lastMessage.content &&
        !lastMessage.content.includes(
          "Are you sure you want to finish this session?"
        ) &&
        !lastMessage.content.includes("Great! Let's continue with our session.")
      ) {
        const soundElement = document.getElementById(
          "message-sound"
        ) as HTMLAudioElement;
        if (soundElement) {
          soundElement.play().catch((error) => {
            logger.error("Error playing message sound:", error);
          });
        }
      }

      // Check for words used in the messages and calculate score ONLY for user messages
      if (onWordUsed && words && words.length > 0) {
        // Get the last message
        const lastMessage = newMessages[newMessages.length - 1];
        if (lastMessage && lastMessage.role === "user") {
          const content = lastMessage.content.toLowerCase();
          let wordsUsed = 0;
          const usedWordTexts: string[] = [];

          // Check each word to see if it was used in the message
          words.forEach((word) => {
            const wordText = word.word.toLowerCase();
            // Only count whole words (with word boundaries)
            const regex = new RegExp(`\\b${wordText}\\b`, "i");
            if (content.match(regex)) {
              onWordUsed(word.id, word.word);
              wordsUsed++;
              usedWordTexts.push(word.word);
            }
          });

          // Calculate sentence quality score (0-5)
          let sentenceScore = 0; // Start with 0

          // 1. Relevance: Did they use any vocabulary words? (0-2 points)
          if (wordsUsed > 0) {
            sentenceScore += 1; // Used at least one word
            if (wordsUsed >= 2) sentenceScore += 1; // Used multiple words
          }

          // 2. Richness: How complex is the sentence? (0-2 points)
          const wordCount = content.split(/\s+/).filter(Boolean).length;

          // Short but valid sentences get 1 point for richness
          if (wordCount >= 3 && wordCount < 8) {
            sentenceScore += 1;
          }
          // Longer, more complex sentences get 2 points for richness
          else if (
            wordCount >= 8 ||
            /[,;:]/.test(content) ||
            /\band\b|\bor\b|\bbut\b|\bbecause\b|\bif\b|\bwhen\b/i.test(content)
          ) {
            sentenceScore += 2;
          }

          // 3. Grammatical correctness (0-1 point)
          // Basic check for sentence structure - starts with uppercase letter (in any language) and ends with punctuation
          if (/^[\p{Lu}A-Z].*[.!?]$/u.test(content.trim())) {
            sentenceScore += 1;
          }

          // Ensure score is between 0 and 5
          sentenceScore = Math.min(5, Math.max(0, sentenceScore));

          // Add score to streak points (only if they score at least 1 point)
          if (sentenceScore > 0) {
            setStreakPoints((prev) => prev + sentenceScore);

            // Trigger confetti for good sentences (score >= 3)
            if (sentenceScore >= 3) {
              triggerConfetti();
            }

            // Show celebration toast with appropriate score and used words
            const wordsList = usedWordTexts.join(", ");
            const message =
              usedWordTexts.length > 0
                ? `${t("chat.usedWords")}: ${wordsList} — +${sentenceScore} ${t(
                    "chat.points"
                  )}`
                : `+${sentenceScore} ${t("chat.points")}`;

            toast({
              title:
                sentenceScore >= 3
                  ? t("chat.excellentSentence")
                  : t("chat.goodSentence"),
              description: message,
              status: sentenceScore >= 3 ? "success" : "info",
              duration: 2000,
              isClosable: true,
              position: "top-right",
            });
          }
        }
      }
    },
    [onUpdateMessages, onWordUsed, t, toast, triggerConfetti, words]
  );

  const createSystemPrompt = useCallback((): ChatMessage => {
    return {
      role: "system",
      content: `You are an AI language tutor helping a user practice ${targetLanguage}.
The user's native language is ${nativeLanguage}.
IMPORTANT: You MUST communicate with the user ONLY in ${nativeLanguage}. Do not use any other language in your responses except when providing examples in ${targetLanguage}.

Your role:
1. Challenge the user to create valid sentences in ${targetLanguage} using words from their vocabulary list
2. Provide short, concise corrections and feedback on their sentences in ${nativeLanguage} only
3. Explain grammar rules only when necessary and keep explanations brief - always in ${nativeLanguage}
4. Encourage the user with positive reinforcement but keep it short and natural
5. Keep the conversation engaging, friendly, and fun
6. Provide helpful tips for improving their language skills but keep them brief
7. Be encouraging, playful, and make learning fun. Use emoji occasionally.
8. Make the session feel like a game with challenges and achievements.

Only use ${targetLanguage} for example sentences, words, or phrases the user needs to learn. All explanations, instructions, and conversation MUST be in ${nativeLanguage} only.

Keep your responses short, conversational, and focused on helping the user improve.
Avoid long explanations unless specifically asked.
Maximum response length should be 2-3 short paragraphs.
Be direct, friendly, and encouraging.`,
    };
  }, [nativeLanguage, targetLanguage]);

  const generateInitialGreeting = useCallback(async () => {
    setIsAiResponding(true);

    const systemPrompt = createSystemPrompt();
    const updatedMessages = [systemPrompt];

    try {
      // Check if user is authenticated using apiClient
      const session = await apiClient.getSession();
      
      if (!session.session) {
        const fallbackMessage: ChatMessage = {
          role: "assistant",
          content: `${t("chat.greeting", { language: targetLanguage })} 👋`,
        };
        updateMessages([...updatedMessages, fallbackMessage]);
        return;
      }

      const payload = {
        messages: updatedMessages,
        words: words || [],
        targetLanguage,
        nativeLanguage,
        timestamp: new Date().toISOString(),
      };

      const data = await apiClient.chatWithAI(payload);
      const aiResponse = data.message;

      const assistantMessage: ChatMessage = {
        role: "assistant",
        content: aiResponse,
      };

      updateMessages([...updatedMessages, assistantMessage]);
      updateSuggestedWords();
    } catch (error) {
      logger.error("Error generating initial greeting:", error);

      const fallbackMessage: ChatMessage = {
        role: "assistant",
        content: `${t("chat.greeting", { language: targetLanguage })} 👋`,
      };
      updateMessages([...updatedMessages, fallbackMessage]);
    } finally {
      setIsAiResponding(false);
    }
  }, [
    createSystemPrompt,
    nativeLanguage,
    t,
    targetLanguage,
    updateMessages,
    updateSuggestedWords,
    words,
  ]);

  useEffect(() => {
    if (didInitializeRef.current) return;
    didInitializeRef.current = true;
    generateInitialGreeting();
    updateSuggestedWords();
  }, [generateInitialGreeting, updateSuggestedWords]);

  // Send a message to the AI
  const handleSendMessage = async () => {
    if (inputMessage.trim() === "") return;

    // Store the current message to avoid closure issues
    const currentMessage = inputMessage.trim();

    // Add user message to chat
    const userMessage: ChatMessage = {
      role: "user",
      content: currentMessage,
    };

    // Clear input field immediately for better UX
    setInputMessage("");

    // Update messages with user message
    const updatedMessages = [...messages, userMessage];
    updateMessages(updatedMessages);

    // Set AI responding state to true to show thinking indicator
    setIsAiResponding(true);

    try {
      const session = await apiClient.getSession();
      
      if (!session.session) {
        logger.info("No authentication token found, using fallback response");
        const fallbackMessage: ChatMessage = {
          role: "assistant",
          content: t("chat.connectionError"),
        };
        updateMessages([...updatedMessages, fallbackMessage]);
        return;
      }

      // Prepare payload with fresh language information
      const payload = {
        messages: updatedMessages,
        words: words || [],
        targetLanguage,
        nativeLanguage,
        timestamp: new Date().toISOString(), // Force fresh context
      };

      // Make API call to get AI response
      logger.info("Making send message API call to AI proxy");
      const data = await apiClient.chatWithAI(payload);
      const aiResponse = data.message;

      // Update chat messages with AI response
      const assistantMessage: ChatMessage = {
        role: "assistant",
        content: aiResponse,
      };
      updateMessages([...updatedMessages, assistantMessage]);

      // Generate new suggested words after each interaction
      updateSuggestedWords();
    } catch (error) {
      logger.error("Error getting AI response:", error);

      // Add a simple fallback response
      const fallbackMessage: ChatMessage = {
        role: "assistant",
        content: t("chat.connectionError"),
      };

      updateMessages([...updatedMessages, fallbackMessage]);

      toast({
        title: t("common.error"),
        description: t("chat.aiError"),
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsAiResponding(false);

      // Return focus to the input field after sending message
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 0);
    }
  };

  // End the practice session and generate a report
  const endPractice = async () => {
    setIsGeneratingReport(true);

    // Let the parent component handle ending the session
    onEndSession();
  };

  // Show confirmation for ending the session
  const handleEndSessionClick = () => {
    setShowEndSessionConfirm(true);

    // Add the confirmation message to the chat
    const confirmMessage: ChatMessage = {
      role: "assistant",
      content:
        t("smartPractice.endSessionConfirm") ||
        "Are you sure you want to finish this session?",
    };

    updateMessages([...messages, confirmMessage]);

    // Scroll to the bottom to show the confirmation
    setTimeout(scrollToBottom, 100);
  };

  // Handle user's response to end session confirmation
  const handleEndSessionResponse = (confirmed: boolean) => {
    if (confirmed) {
      // User confirmed, end the session
      endPractice();
    } else {
      // User declined, continue the session
      setShowEndSessionConfirm(false);

      // Add a message indicating they'll continue
      const continueMessage: ChatMessage = {
        role: "assistant",
        content:
          t("smartPractice.sessionContinue") ||
          "Great! Let's continue with our session.",
      };

      updateMessages([...messages, continueMessage]);
    }
  };

  // Toggle sidebar function
  const handleToggleSidebar = () => {
    // Create a specific event that indicates we want to toggle from the chat interface
    const event = new CustomEvent("chat-toggle-sidebar");
    document.dispatchEvent(event);

    // We'll rely on the layout component to update the state, but let's also update our local state
    setIsSidebarOpen((prev) => !prev);
  };

  // Update streaming message
  const updateStreamingMessage = (token: string, isFinalToken: boolean) => {
    setMessages((prevMessages) => {
      const lastMessage = prevMessages[prevMessages.length - 1];

      // Play sound when the first token of a streaming message arrives
      if (lastMessage.role === "assistant" && lastMessage.content === "") {
        const soundElement = document.getElementById(
          "message-sound"
        ) as HTMLAudioElement;
        if (soundElement) {
          soundElement.play().catch((error) => {
            logger.error("Error playing message sound:", error);
          });
        }
      }

      // If the last message is from the assistant, update it
      if (lastMessage.role === "assistant") {
        const updatedMessages = [...prevMessages];
        const updatedLastMessage = { ...lastMessage };
        updatedLastMessage.content += token;
        updatedMessages[updatedMessages.length - 1] = updatedLastMessage;
        return updatedMessages;
      }

      return prevMessages;
    });
  };

  // Handle adding a word to the input
  const handleAddWord = useCallback((word: string) => {
    setInputMessage((prev) => {
      const space = prev.length > 0 && !prev.endsWith(" ") ? " " : "";
      return `${prev}${space}${word} `;
    });

    // Focus the input after adding the word
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  return (
    <Box
      bg={chatContainerBg}
      borderRadius={{ base: "none", md: "xl" }}
      overflow="hidden"
      boxShadow={{ base: "none", md: chatBoxShadow }}
      height="100%"
      display="flex"
      flexDirection="column"
      width="100%"
      position="relative"
      m="0"
      p="0"
      transition="all 0.5s cubic-bezier(0.17, 0.67, 0.83, 0.67)"
      dir={isRTL ? "rtl" : "ltr"}
      className="chat-interface"
      ref={confettiCanvasRef}
    >
      {/* Chat Header */}
      <Flex
        p={{ base: 3, md: 4 }}
        bg={headerFooterBg}
        borderBottom="1px"
        borderColor={borderColor}
        align="center"
        justify="space-between"
      >
        <HStack spacing={4}>
          <Box>
            <Heading size="md">{t("smartPractice.aiTutor")}</Heading>
            <HStack>
              <Badge colorScheme="green" borderRadius="full" px={2} py={0.5}>
                {targetLanguage}
              </Badge>
              <Text fontSize="xs" color="gray.500">
                {words?.length || 0} {t("common.words").toLowerCase()}
              </Text>
            </HStack>
          </Box>
        </HStack>

        {/* Stream points tracker */}
        <Tooltip label={t("chat.streakPoints")} hasArrow>
          <HStack
            bg={streakBg}
            borderRadius="full"
            px={3}
            py={1}
            color={accentColor}
            fontWeight="bold"
            animation={streakPoints > 0 ? `${pulse} 0.5s ease` : undefined}
            transition="all 0.3s ease"
          >
            <Icon as={FiStar} />
            <Text>{streakPoints}</Text>
          </HStack>
        </Tooltip>
      </Flex>

      {/* Chat Messages */}
      <Box
        flex="1"
        overflow="auto"
        px={{ base: 2, md: 4 }}
        py={{ base: 1, md: 2 }}
        ref={chatContainerRef}
        sx={chatContainerStyles}
        className="scroll-container"
        display="flex"
        flexDirection="column"
        height={{ base: "calc(100% - 130px)", md: "calc(100% - 140px)" }}
      >
        {messages.map((message, index) => {
          // Skip system messages
          if (message.role === "system") return null;

          const isLast = index === messages.length - 1;

          // If this is the confirmation message, render it with the confirmation buttons
          if (
            isLast &&
            showEndSessionConfirm &&
            message.content ===
              (t("smartPractice.endSessionConfirm") ||
                "Are you sure you want to finish this session?")
          ) {
            return (
              <Box key={index} width="100%">
                <ChatMessage message={message} isLast={false} />

                {/* Confirmation buttons */}
                <Flex
                  mt={2}
                  justifyContent="center"
                  alignItems="center"
                  gap={4}
                  p={4}
                  bg={confirmationBg}
                  borderRadius="xl"
                  boxShadow="md"
                  animation={`${fadeIn} 0.3s ease-in-out`}
                  position="relative"
                  maxW="90%"
                  mx="auto"
                  flexDirection="column"
                  border="1px solid"
                  borderColor={confirmationBorderColor}
                >
                  <Icon
                    as={FiMessageSquare}
                    position="absolute"
                    top="-12px"
                    color={colors.primary[500]}
                    bg={iconBg}
                    p={1}
                    borderRadius="full"
                    boxSize="24px"
                  />

                  <Text textAlign="center" fontWeight="medium" mb={3}>
                    {t("smartPractice.endSessionConfirm") ||
                      "Are you sure you want to finish this session?"}
                  </Text>

                  <Flex gap={3} width="100%" justifyContent="center">
                    <Button
                      colorScheme="red"
                      onClick={() => handleEndSessionResponse(true)}
                      size="md"
                      leftIcon={<FiX />}
                      isLoading={isGeneratingReport}
                      loadingText={t("smartPractice.generatingReport")}
                      flex="1"
                      maxW="150px"
                    >
                      {t("common.yes") || "Yes"}
                    </Button>

                    <Button
                      colorScheme="brand"
                      onClick={() => handleEndSessionResponse(false)}
                      size="md"
                      flex="1"
                      maxW="150px"
                    >
                      {t("common.no") || "No"}
                    </Button>
                  </Flex>
                </Flex>
              </Box>
            );
          }

          return (
            <ScaleFade key={index} in={true} initialScale={0.9}>
              <ChatMessage message={message} isLast={isLast} />
            </ScaleFade>
          );
        })}

        {/* Typing indicator */}
        {isAiResponding && <TypingIndicator />}

        <div ref={messagesEndRef} />
      </Box>

      {/* Word Bubbles */}
      <Box
        p={{ base: 2, md: 3 }}
        bg={headerFooterBg}
        borderTop="1px"
        borderColor={borderColor}
      >
        <Flex justifyContent="space-between" alignItems="center">
          <Wrap spacing={{ base: 1, md: 2 }} flex="1">
            {suggestedWords.map((word, index) => (
              <WrapItem key={word.id}>
                <WordBubble
                  word={word.word}
                  translation={word.translation}
                  onClick={() => handleAddWord(word.word)}
                  index={index}
                />
              </WrapItem>
            ))}
          </Wrap>

          {/* End conversation button */}
          <IconButton
            aria-label={t("smartPractice.endConversation")}
            icon={<FiLogOut />}
            colorScheme="red"
            variant="solid"
            ml={isRTL ? 0 : 2}
            mr={isRTL ? 2 : 0}
            isDisabled={showEndSessionConfirm || isGeneratingReport}
            onClick={handleEndSessionClick}
            size="md"
            title={t("smartPractice.endConversation")}
            _hover={{
              transform: "translateY(-2px)",
              boxShadow: "md",
            }}
            transition="all 0.2s ease"
          />
        </Flex>
      </Box>

      {/* Input Area */}
      <Box
        p={{ base: 2, md: 4 }}
        bg={headerFooterBg}
        borderTop="1px"
        borderColor={borderColor}
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSendMessage();
          }}
        >
          <InputGroup size={{ base: "md", md: "lg" }}>
            <Input
              placeholder={t("smartPractice.typeMessage")}
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              bg={inputBg}
              borderRadius="full"
              pr={{ base: "3.5rem", md: "4.5rem" }}
              borderWidth="1px"
              borderColor={borderColor}
              fontSize={{ base: "sm", md: "md" }}
              py={{ base: 1, md: 2 }}
              _focus={{
                borderColor: colors.primary[500],
                boxShadow: `0 0 0 1px ${colors.primary[500]}`,
              }}
              _hover={{
                borderColor: colors.primary[400],
              }}
              disabled={isAiResponding || isGeneratingReport}
              ref={inputRef}
            />
            <InputRightElement width={{ base: "3.5rem", md: "4.5rem" }}>
              <IconButton
                h="1.75rem"
                size="sm"
                aria-label={t("common.send")}
                icon={<FiSend />}
                type="submit"
                colorScheme="brand"
                borderRadius="full"
                isDisabled={
                  inputMessage.trim() === "" ||
                  isAiResponding ||
                  isGeneratingReport
                }
                _hover={{
                  transform: "translateY(-2px)",
                  bg: colors.primary[400],
                }}
                transition="all 0.2s"
              />
            </InputRightElement>
          </InputGroup>
        </form>
      </Box>

      {/* Hidden audio element for message sound */}
      <audio
        src="/sounds/message.mp3"
        id="message-sound"
        style={{ display: "none" }}
      />
    </Box>
  );
}
